import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, MapPin } from "lucide-react";
import { Reveal, MaskedLines, SectionLabel } from "@/components/Reveal";
import { IMAGES, STATS, PROCESS_STRIP, WHY_AURIC, CLIENTS, LOCATIONS } from "@/data/content";

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-ink" data-testid="home-hero">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img src={IMAGES.hero} alt="Modern corporate architecture" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-black/45" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-ink/30" />

      <motion.div style={{ opacity }} className="relative h-full max-w-7xl mx-auto px-6 sm:px-12 flex flex-col justify-end pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="inline-flex items-center gap-2.5 self-start bg-white/95 px-5 py-3 mb-8 shadow-lg shadow-black/20"
          data-testid="hero-label"
        >
          <span className="text-sm tracking-[0.3em] uppercase font-extrabold text-gold">Auric</span>
          <span className="text-sm tracking-[0.3em] uppercase font-bold text-ink">Valuation Pvt. Ltd.</span>
        </motion.p>
        <h1 className="font-serif text-cream text-5xl sm:text-7xl lg:text-8xl tracking-tight leading-[0.98]">
          <MaskedLines lines={["Value Beyond", "Numbers."]} delay={0.35} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-200"
        >
          Professional valuation services across Rajasthan and beyond — delivering reliable
          valuation for informed decisions.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <Link
            to="/services"
            data-testid="hero-services-button"
            className="bg-gold text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 inline-flex items-center gap-2 hover:bg-cream transition-colors duration-300"
          >
            Explore Services <ArrowRight size={14} />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-contact-button"
            className="border border-cream/40 text-cream text-xs tracking-[0.25em] uppercase font-semibold px-8 py-4 hover:border-gold-light hover:text-gold-light transition-colors duration-300"
          >
            Request Valuation
          </Link>
        </motion.div>
      </motion.div>
    </section>
  );
};

const ClientMarquee = () => (
  <section className="py-16 border-b border-border bg-cream" data-testid="client-marquee-section">
    <p className="text-center font-serif text-xl sm:text-2xl font-bold text-ink mb-12 px-6" data-testid="client-marquee-headline">
      Empanelled with <span className="text-gold">45+</span> Leading Institutions
    </p>
    <Marquee speed={30} gradient={false} pauseOnHover>
      {CLIENTS.map((c) => (
        <span key={c.name} className="mx-10 inline-flex h-28 items-center">
          <img src={c.logo} alt={`${c.name} logo`} className="max-h-24 w-auto object-contain" loading="lazy" />
        </span>
      ))}
    </Marquee>
  </section>
);

const StatsBento = () => (
  <section className="py-24 sm:py-32 px-6 sm:px-12" data-testid="stats-section">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <SectionLabel>Company at a Glance</SectionLabel>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-ink">
              A focused valuation company, built on trust.
            </h2>
            <p className="mt-8 text-base leading-relaxed text-slate-600">
              Auric Valuation Pvt. Ltd. is a Jaipur-based real estate valuation company focused on
              professional land and building valuation assignments — serving financial institutions,
              NBFCs, corporates and property stakeholders with practical understanding of local
              property markets and lender requirements.
            </p>
            <Link
              to="/about"
              data-testid="stats-about-link"
              className="mt-8 inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-semibold text-gold hover:text-ink transition-colors duration-300"
            >
              More About Us <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-px bg-border border border-border">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.08} className="bg-white">
              <div className="p-8 h-full hover:-translate-y-1 transition-transform duration-300" data-testid={`stat-card-${i}`}>
                <p className="font-serif text-3xl sm:text-4xl font-bold text-gold">{s.value}</p>
                <p className="mt-3 text-xs leading-relaxed text-slate-500">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const ProcessStrip = () => (
  <section className="bg-white border-y border-border py-24 px-6 sm:px-12" data-testid="process-strip">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>How Every Assignment Flows</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink max-w-2xl">
          From ground truth to defensible opinion.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        {PROCESS_STRIP.map((p, i) => (
          <Reveal key={p.title} delay={i * 0.1} className="bg-white">
            <div className="p-10 group hover:bg-ink transition-colors duration-500 h-full" data-testid={`process-card-${i}`}>
              <p className="font-serif text-5xl text-gold/40 group-hover:text-gold transition-colors duration-500">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="mt-6 font-serif text-xl text-ink group-hover:text-cream transition-colors duration-500">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-500 group-hover:text-slate-400 transition-colors duration-500">{p.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyAuric = () => (
  <section className="py-24 sm:py-32 px-6 sm:px-12" data-testid="why-auric-section">
    <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
      <div className="lg:col-span-5">
        <Reveal>
          <div className="clipped-frame">
            <img src={IMAGES.services} alt="High-rise building against the sky" className="w-full h-[420px] object-cover" />
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-7">
        <Reveal>
          <SectionLabel>Why Auric</SectionLabel>
          <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink">
            Reports that stand up to scrutiny.
          </h2>
        </Reveal>
        <div className="mt-12 divide-y divide-border border-y border-border">
          {WHY_AURIC.map((w, i) => (
            <Reveal key={i} delay={i * 0.05} y={16}>
              <div className="py-6 flex gap-6 items-baseline group" data-testid={`why-item-${i}`}>
                <span className="font-serif text-lg text-gold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-base leading-relaxed text-slate-600 group-hover:text-ink transition-colors duration-300">{w}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Locations = () => (
  <section className="py-24 px-6 sm:px-12 bg-white border-t border-border" data-testid="home-locations-section">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <SectionLabel>Service Locations</SectionLabel>
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-ink max-w-2xl">
          Serving major cities and districts across Rajasthan and beyond.
        </h2>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-8 gap-y-5">
        {LOCATIONS.map((l, i) => (
          <Reveal key={l} delay={(i % 8) * 0.04} y={12}>
            <p className="flex items-center justify-center gap-2 text-center font-serif text-base font-semibold text-ink border-b border-border pb-3 hover:text-gold hover:border-gold transition-colors duration-300" data-testid={`home-location-${i}`}>
              <MapPin size={16} className="text-gold shrink-0" />
              {l}
            </p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBand = () => (
  <section className="bg-ink text-cream py-24 sm:py-32 px-6 sm:px-12 relative overflow-hidden" data-testid="cta-band">
    <div
      className="absolute inset-0 opacity-[0.05] pointer-events-none"
      style={{ backgroundImage: "linear-gradient(#faf9f6 1px, transparent 1px), linear-gradient(90deg, #faf9f6 1px, transparent 1px)", backgroundSize: "80px 80px" }}
    />
    <div className="max-w-7xl mx-auto relative">
      <Reveal>
        <SectionLabel light>Start a Conversation</SectionLabel>
        <h2 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
          Delivering reliable valuation for informed decisions.
        </h2>
        <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300">
          35+ key locations across Rajasthan and beyond. One experienced valuation manager as your
          single point of coordination.
        </p>
        <Link
          to="/contact"
          data-testid="cta-contact-button"
          className="mt-10 inline-flex items-center gap-2 bg-gold text-white text-xs tracking-[0.25em] uppercase font-bold px-8 py-4 hover:bg-cream transition-colors duration-300"
        >
          Get in Touch <ArrowRight size={14} />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <ClientMarquee />
      <StatsBento />
      <ProcessStrip />
      <WhyAuric />
      <Locations />
      <CtaBand />
    </>
  );
}
